<template>
  <div class="pa-0">
    <!-- Header Section -->
    <div class="primary pa-6 d-flex align-center">
      <v-avatar color="white lighten-2" size="40" class="mr-4 shadow-sm" style="background-color: rgba(255,255,255,0.2) !important;">
        <v-icon color="white" size="20">fas fa-file-invoice</v-icon>
      </v-avatar>
      <div>
        <div class="text-h5 font-weight-black white--text lh-1">{{ isNC ? 'Detalle de la Nota de Crédito' : 'Detalle del Documento' }}</div>
        <div class="caption white--text opacity-80 uppercase tracking-widest mt-1">
          {{ isNC ? 'Nota de Crédito' : '' }} Folio #{{invoice.document_id}} — {{invoice.rznSocRecep}}
          <template v-if="isNC && invoice.motivo_display"> · {{ invoice.motivo_display }}</template>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div v-if="isGuia && hasRelatedInvoices" class="d-flex align-center mr-3">
        <v-chip color="success" dark class="font-weight-black shadow-sm" label small>
          <v-icon x-small left color="white">fas fa-check-circle</v-icon>
          Facturada ({{ invoice.related_invoices.map(i => '#' + (i.folio || i.document_id)).join(', ') }})
        </v-chip>
      </div>
      <v-btn
        v-if="isGuia"
        color="white"
        outlined
        class="font-weight-black mr-4 shadow-sm text-none"
        @click="$emit('bill-guia', invoice)"
      >
        <v-icon left small>fas fa-file-invoice-dollar</v-icon>
        {{ hasRelatedInvoices ? 'Facturar de Nuevo' : 'Facturar Guía' }}
      </v-btn>
      <v-btn icon @click="$emit('close')" dark large>
        <v-icon>fas fa-times</v-icon>
      </v-btn>
    </div>

    <div class="pa-8">
      <v-row>
        <!-- Info Sidebar -->
        <v-col cols="12" md="4" v-if="!inSet">
          <v-card outlined class="modern-card pa-6 bg-lightgrey mb-6">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">Información General</div>
            
            <div class="d-flex flex-column gap-3">
              <div class="info-row">
                <span class="caption grey--text font-weight-bold">Fecha Emisión</span>
                <span class="body-2 font-weight-black primary--text">{{invoice.date_emited|moment('DD/MM/YYYY')}}</span>
              </div>
              <div class="info-row" v-if="invoice.payment_date">
                <span class="caption grey--text font-weight-bold">Vencimiento</span>
                <span class="body-2 font-weight-black primary--text">{{invoice.payment_date|moment('DD/MM/YYYY')}}</span>
              </div>
              <div class="info-row">
                <span class="caption grey--text font-weight-bold">RUT Receptor</span>
                <span class="body-2 font-weight-black primary--text">{{invoice.rutRecep}}</span>
              </div>
              <div class="info-row">
                <span class="caption grey--text font-weight-bold">Razón Social</span>
                <span class="caption font-weight-black primary--text lh-1-2 pl-2">{{invoice.rznSocRecep}}</span>
              </div>
            </div>

            <!-- Bloque de Documento Referenciado (Para Facturas que referencian Guía / OC) -->
            <template v-if="hasReferencia">
              <v-divider class="my-6"></v-divider>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">
                <v-icon x-small color="primary" class="mr-1">fas fa-link</v-icon>
                {{ isNC ? 'Documento que modifica' : 'Documento Referenciado' }}
              </div>
              <div class="pa-3 rounded-lg border-light bg-white">
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="caption grey--text font-weight-bold">Tipo Doc.</span>
                  <v-chip x-small :color="(Number(invoice.referencia_tipo) === 50 || Number(invoice.referencia_tipo) === 52 ? 'info' : 'primary') + ' lighten-5'" :class="(Number(invoice.referencia_tipo) === 50 || Number(invoice.referencia_tipo) === 52 ? 'info' : 'primary') + '--text font-weight-black'" label>
                    <v-icon x-small left>{{ (Number(invoice.referencia_tipo) === 50 || Number(invoice.referencia_tipo) === 52) ? 'fas fa-truck' : 'fas fa-file-invoice' }}</v-icon>
                    {{ referenciaDocLabel }}
                  </v-chip>
                </div>
                <div class="d-flex align-center justify-space-between mb-3">
                  <span class="caption grey--text font-weight-bold">N° Folio</span>
                  <span class="body-1 font-weight-black primary--text">#{{ invoice.referencia_folio }}</span>
                </div>
                <v-btn
                  small block color="primary" outlined
                  class="font-weight-bold text-none"
                  @click="$emit('search-doc', invoice.referencia_folio)"
                >
                  <v-icon x-small left>fas fa-search</v-icon>
                  Ver {{ referenciaDocLabel }} #{{ invoice.referencia_folio }}
                </v-btn>
              </div>
            </template>

            <!-- Bloque de Factura(s) Relacionada(s) (Para Guías de Despacho) -->
            <template v-if="isGuia && hasRelatedInvoices">
              <v-divider class="my-6"></v-divider>
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">
                <v-icon x-small color="success" class="mr-1">fas fa-file-invoice-dollar</v-icon>
                Factura(s) Relacionada(s)
              </div>
              <div class="d-flex flex-column gap-2">
                <div
                  v-for="(inv, idx) in invoice.related_invoices"
                  :key="'rel-inv-' + idx"
                  class="pa-3 rounded-lg border-light bg-white"
                >
                  <div class="d-flex align-center justify-space-between mb-1">
                    <span class="caption font-weight-black success--text">{{ inv.tipo_display || 'Factura Electrónica' }}</span>
                    <span class="body-2 font-weight-black primary--text">#{{ inv.folio || inv.document_id }}</span>
                  </div>
                  <div v-if="inv.total" class="d-flex align-center justify-space-between caption grey--text mb-1">
                    <span>Total Facturado:</span>
                    <span class="font-weight-bold primary--text">{{ inv.total | moneda }}</span>
                  </div>
                  <div v-if="inv.date" class="caption grey--text mb-2">
                    <span>Fecha Emisión: {{ inv.date }}</span>
                  </div>
                  <v-btn
                    x-small block color="primary" outlined
                    class="font-weight-bold text-none"
                    @click="$emit('search-doc', inv.folio || inv.document_id)"
                  >
                    <v-icon x-small left>fas fa-external-link-alt</v-icon>
                    Ver Factura #{{ inv.folio || inv.document_id }}
                  </v-btn>
                </div>
              </div>
            </template>

            <v-divider class="my-6"></v-divider>

            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">Montos y Saldos</div>
            <div class="d-flex flex-column gap-3">
              <div class="info-row">
                <span class="caption grey--text font-weight-bold">Monto Neto</span>
                <span class="body-2 font-weight-black primary--text">{{invoice.get_subtotal| moneda}}</span>
              </div>
              <div class="info-row">
                <span class="caption grey--text font-weight-bold">IVA ({{invoice.iva}}%)</span>
                <span class="body-2 font-weight-black primary--text">{{invoice.get_iva| moneda}}</span>
              </div>
              <div class="info-row total-row pa-2 rounded-lg mt-2">
                <span class="caption primary--text font-weight-black">TOTAL BRUTO</span>
                <span class="text-h6 font-weight-black primary--text">{{invoice.get_total| moneda}}</span>
              </div>
            </div>

            <v-divider v-if="!isNC" class="my-6"></v-divider>

            <div v-if="!isNC" class="d-flex flex-column gap-3">
              <div class="info-row">
                <span class="caption success--text font-weight-black">PAGADO</span>
                <span class="body-1 font-weight-black success--text">{{invoice.get_payed| moneda}}</span>
              </div>
              <div class="info-row pa-2 rounded-lg" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error lighten-5' : 'success lighten-5'">
                <span class="caption font-weight-black" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error--text' : 'success--text'">SALDO PENDIENTE</span>
                <span class="body-1 font-weight-black" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error--text' : 'success--text'">{{invoice.get_total-invoice.get_payed| moneda}}</span>
              </div>
            </div>
          </v-card>
        </v-col>

        <!-- Products Table -->
        <v-col cols="12" :md="inSet?12:8">
          <v-card outlined class="modern-card overflow-hidden">
            <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
              <span class="caption font-weight-black uppercase tracking-widest grey--text">{{ isNC ? 'Líneas de la Nota de Crédito' : 'Desglose de Productos / Servicios' }}</span>
              <v-spacer></v-spacer>
              <span v-if="invoice.payment_date" class="caption font-weight-bold grey--text">
                Vence {{ invoice.payment_date | moment('DD/MM/YYYY') }}
              </span>
            </div>
            
            <v-data-table
              :headers="headers"
              :items="invoice.details"
              :items-per-page="-1"
              :loading="loading"
              class="modern-table no-footer"
              hide-default-footer
            >
              <template v-slot:no-data>
                <div class="pa-12 text-center grey--text">
                  <v-icon size="48" color="grey lighten-3" class="mb-4">fas fa-box-open</v-icon>
                  <p class="body-1 font-weight-bold">No hay detalles disponibles</p>
                </div>
              </template>

              <template v-slot:item.sku="{ item }">
                <span class="caption font-weight-black primary--text tracking-tighter">{{item.sku}}</span>
              </template>

              <template v-slot:item.name="{ item }">
                <span class="body-2 font-weight-bold grey--text text--darken-3 lh-1-2 d-block py-2">{{item.name}}</span>
              </template>

              <template v-slot:item.qty="{ item }">
                <v-chip x-small outlined color="grey darken-1" class="font-weight-black">{{item.qty| number('0.00')}}</v-chip>
              </template>

              <template v-slot:item.price="{ item }">
                <span class="body-2 font-weight-medium">{{item.price| moneda}}</span>
              </template>

              <template v-slot:item.total="{ item }">
                <span class="body-2 font-weight-black primary--text">{{item.price*item.qty| moneda}}</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvoiceDetail',
  props: ['invoice', 'inSet'],
  data() {
    return {
      loading: false,
      headers: [
        { text: '#', align: 'start', value: 'nro_linea', class: 'table-header', width: '60px', sortable: false },
        { text: 'Código / SKU', align: 'start', value: 'sku', class: 'table-header', width: '140px', sortable: false },
        { text: 'Descripción del Item', align: 'start', value: 'name', class: 'table-header', sortable: false },
        { text: 'Cant.', align: 'end', value: 'qty', class: 'table-header', width: '90px', sortable: false },
        { text: 'P. Unitario', align: 'end', value: 'price', class: 'table-header', width: '130px', sortable: false },
        { text: 'Importe', align: 'end', value: 'total', class: 'table-header', width: '130px', sortable: false },
      ],
    }
  },
  computed: {
    isGuia() {
      if (!this.invoice) return false
      const tipo = Number(this.invoice.sf_tipo || this.invoice.tipo)
      return (tipo === 50 || tipo === 52) && Number(this.invoice.estatus) !== 3
    },
    isNC() {
      const tipo = Number(this.invoice && (this.invoice.sf_tipo || this.invoice.tipo))
      return tipo === 60 || tipo === 61
    },
    hasRelatedInvoices() {
      return this.invoice && Array.isArray(this.invoice.related_invoices) && this.invoice.related_invoices.length > 0
    },
    hasReferencia() {
      return this.invoice && !!this.invoice.referencia_folio
    },
    referenciaDocLabel() {
      if (!this.invoice || !this.invoice.referencia_tipo) return 'Documento'
      const t = Number(this.invoice.referencia_tipo)
      if (t === 50 || t === 52) return 'Guía de Despacho'
      if (t === 30 || t === 33) return 'Factura'
      if (t === 39) return 'Boleta'
      if (t === 801) return 'Orden de Compra'
      if (t === 802) return 'Nota de Pedido'
      if (t === 803) return 'Contrato'
      return this.invoice.referencia_tipo_display || `Doc (${t})`
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.opacity-80 { opacity: 0.8; }

.info-row { display: flex; justify-content: space-between; align-items: flex-start; }
.total-row { background-color: #eef2f5; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 48px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table.no-footer >>> .v-data-footer { display: none; }
</style>